import React, { useMemo, useCallback, useState } from "react";
import { Typography, Paper, CircularProgress, Box } from "@mui/material";

import {
  getTableConfig,
  DEFAULT_PAGINATION,
  TABLE_TYPES,
} from "../config/tableConfig";
import { useTableData, useUpdateTableStatus } from "../hooks/useTableData";
import { useTableFilters } from "../hooks/useTableFilters";
import { useTableDialogs } from "../hooks/useTableDialogs";

import TableView from "./TableView";
import MobileView from "./MobileView";
import DialogsSection from "./DialogsSection";
import PaginationInfoView from "./PaginationInfo";
import OnboardingFilters from "./OnboardingFilters";
import PreOnboardingDetails from "../details/PreOnboardingDetails";
import ActionBar from "./ActionBar";
import DataTablePagination from "./Pagination";
import StatsStrip from "./StatsStrip";
import AlertBanner from "../../../components/Alert/AlertBanner";
import { buildTableStats } from "../../utils/buildTableStats";
import { useDeleteDriverLead } from "../../../hooks/use-driver";
import LeadFeedback from "../../driverLeadCreation/LeadFeedback";
import LeadDetails from "../../driverLeadCreationV2/LeadDetails";

const TableTypeView = ({ tableType, isMobile, user, navigate, paths }) => {
  const userEmail = user?.email || "Unknown User";
  const config = getTableConfig(tableType);

  const filterState = useTableFilters(tableType);
  const dialogs = useTableDialogs();

  const [isFinancierChangeRequest, setIsFinancierChangeRequest] =
    useState(false);

  const {
    data: apiResponse = { data: [], pagination: DEFAULT_PAGINATION },
    isLoading,
    isFetching,
    error,
    refetch,
  } = useTableData(
    tableType,
    filterState.apiParams,
    filterState.rowsPerPage,
    filterState.currentOffset,
  );

  const dataList = useMemo(() => apiResponse.data || [], [apiResponse]);
  const paginationInfo = useMemo(
    () => apiResponse.pagination || DEFAULT_PAGINATION,
    [apiResponse.pagination],
  );
  const statusCounts = useMemo(
    () => apiResponse?.statusCounts,
    [apiResponse.statusCounts],
  );
  const onboardingStatusCounts = useMemo(
    () => apiResponse?.onboardingStatusCounts,
    [apiResponse.onboardingStatusCounts],
  );

  const { stats, groups } = useMemo(
    () =>
      buildTableStats(config.stats, {
        paginationInfo,
        statusCounts,
        onboardingStatusCounts,
      }),
    [config.stats, paginationInfo, statusCounts, onboardingStatusCounts],
  );

  const { mutate: updateStatus, isPending: isUpdating } =
    useUpdateTableStatus(tableType);

  const { mutate: deleteLead, isPending: isLeadDeleting } =
    useDeleteDriverLead();

  const handleStatusUpdate = useCallback(
    (newStatus) => {
      if (!dialogs.statusDialog.data?.id) return;
      updateStatus(
        {
          id: dialogs.statusDialog.data.id,
          status: newStatus,
          updatedBy: userEmail,
        },
        {
          onSuccess: () => {
            dialogs.statusDialog.onClose();
            refetch();
          },
        },
      );
    },
    [
      dialogs.statusDialog.data,
      updateStatus,
      dialogs.statusDialog.onClose,
      refetch,
    ],
  );

  const handleUpdateData = (leadId) => {
    if (leadId) navigate(`${paths.DRIVER_LEAD_CREATION}?leadId=${leadId}`);
  };

  const handleDelete = useCallback(
    (id) => {
      if (tableType === TABLE_TYPES.LEADS) {
        deleteLead(
          {
            leadId: id,
          },
          {
            onSuccess: () => {
              dialogs.deleteDialog.onClose();
              refetch();
            },
          },
        );
      } else {
        updateStatus(
          { id, status: "Deleted", updatedBy: userEmail },
          {
            onSuccess: () => {
              dialogs.deleteDialog.onClose();
              refetch();
            },
          },
        );
      }
    },
    [updateStatus, dialogs.deleteDialog.onClose, refetch],
  );

  const handleFinancierChangeRequest = useCallback((id) => {
    setIsFinancierChangeRequest(true);
    updateStatus(
      { id, status: "FINANCIER_CHANGE_REQUEST", updatedBy: userEmail },
      {
        onSuccess: () => {
          setIsFinancierChangeRequest(false);
          dialogs.financierChangeRequestDialog.onClose();
          refetch();
        },
        onError: (error) => {
          console.log("error: ", error);
          setIsFinancierChangeRequest(false);
        },
      },
    );
  });

  const searchFilterKey = tableType === TABLE_TYPES.LEADS ? "leadId" : "prn";

  const handlePreOnboardDoc = (leadId) => {
    navigate(paths.LEAD_DOCUMENTATION.replace(":leadId", leadId));
  };

  const handlePreOnboardUpdate = (prn) => {
    if (prn) {
      navigate(paths.PRE_ONBOARDING_UPDATE.replace(":prn", prn));
    }
  };

  const Loader = ({ overlay = false }) =>
    overlay ? (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          zIndex: 1000,
          borderRadius: 3,
        }}
      >
        <CircularProgress />
      </Box>
    ) : (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );

  const ErrorState = () =>
    error && (
      <Paper sx={{ p: 2, backgroundColor: "#ffebee", borderRadius: 2, mb: 2 }}>
        <Typography color="error">Error: {error.message}</Typography>
      </Paper>
    );

  const EmptyState = () => (
    <Paper
      sx={{
        p: 4,
        textAlign: "center",
        backgroundColor: "#f5f5f5",
        borderRadius: 2,
        mt: isMobile ? 0 : 2,
      }}
    >
      <Typography variant="body2" color="textSecondary">
        No {config.title.toLowerCase()} applications found
      </Typography>
    </Paper>
  );

  const getDetailsComponent = () => {
    switch (tableType) {
      case TABLE_TYPES.PRE_ONBOARDING:
        return PreOnboardingDetails;
      case TABLE_TYPES.LEADS:
        return LeadDetails;
      default:
        return undefined;
    }
  };

  // if (isLoading) {
  //   return <Loader />;
  // }

  return (
    <>
      <PaginationInfoView
        paginationInfo={paginationInfo}
        currentPage={filterState.currentPage}
      />

      <OnboardingFilters
        filters={filterState.filters}
        filterSearchValue={filterState.filters[searchFilterKey]}
        onFilterChange={(id, value) => filterState.updateFilter(id, value)}
        onSearchChange={(value) =>
          filterState.updateFilter(searchFilterKey, value)
        }
        onClearFilters={filterState.clearFilters}
        onRefresh={refetch}
        isFetching={isFetching}
        isMobile={isMobile}
        tableType={tableType}
      />

      <ErrorState />

      {tableType !== TABLE_TYPES.ONBOARDED && (
        <StatsStrip stats={stats} groups={groups} />
      )}

      {tableType === TABLE_TYPES.LEADS && (
        <AlertBanner
          count={onboardingStatusCounts?.DOC_CHANGE_REQUESTED ?? 0}
          label={
            (onboardingStatusCounts?.DOC_CHANGE_REQUESTED ?? 0) === 1
              ? "lead needs changes"
              : "leads need changes"
          }
          description="Review and resubmit before they can move forward."
          onClick={() => {
            filterState.updateFilter(
              "onboardingStatus",
              "DOC_CHANGE_REQUESTED",
            );
            filterState.setCurrentPage(0);
          }}
        />
      )}

      <Box sx={{ position: "relative" }}>
        {(isFetching || isLoading) && <Loader overlay />}

        {isMobile ? (
          <MobileView
            tableType={tableType}
            data={dataList}
            isFetching={isFetching}
            dialogs={dialogs}
            onMenuOpen={
              tableType === TABLE_TYPES.PRE_ONBOARDING ||
              tableType === TABLE_TYPES.LEADS
                ? dialogs.menu.onOpen
                : dialogs.cardAddition.onOpen
            }
          />
        ) : (
          <TableView
            tableType={tableType}
            data={dataList}
            isLoading={isLoading}
            isFetching={isFetching}
            dialogs={dialogs}
            onMenuOpen={
              tableType === TABLE_TYPES.PRE_ONBOARDING ||
              tableType === TABLE_TYPES.LEADS
                ? dialogs.menu.onOpen
                : dialogs.cardAddition.onOpen
            }
          />
        )}

        {dataList.length === 0 && <EmptyState />}

        <DataTablePagination
          isMobile={isMobile}
          dataList={dataList}
          paginationInfo={paginationInfo}
          currentPage={filterState.currentPage}
          handlePageChange={filterState.handlePageChange}
          rowsPerPage={filterState.rowsPerPage}
          handleRowsPerPageChange={filterState.handleRowsPerPageChange}
        />
      </Box>

      <DialogsSection
        dialogs={dialogs}
        tableType={tableType}
        DetailsComponent={getDetailsComponent()}
        ActionBarComponent={ActionBar}
        onStatusUpdate={handleStatusUpdate}
        onUpdateData={handleUpdateData}
        onDelete={handleDelete}
        statusUpdateLoading={isUpdating}
        deleteLoading={isUpdating}
        deleteError={error}
        refetchTableData={refetch}
        onPreOnboardDoc={handlePreOnboardDoc}
        leadFeedbackComponent={LeadFeedback}
        onFinancierChangeRequest={handleFinancierChangeRequest}
        financierChangeRequestLoading={isFinancierChangeRequest}
        onAllUpdate={handlePreOnboardUpdate}
      />
    </>
  );
};

export default TableTypeView;
